import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowLeft, Share2, Linkedin, Twitter, Facebook } from 'lucide-react';
import { articlesService } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadArticle();
  }, [slug]);

  const loadArticle = async () => {
    try {
      const response = await articlesService.getBySlug(slug);
      setArticle(response.data.article);
    } catch (error) {
      console.error('Erreur:', error);
      setError('Article non trouvé');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    try {
      return format(new Date(date), 'd MMMM yyyy', { locale: fr });
    } catch {
      return '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" text="Chargement..." />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-dark-900 mb-4">Article non trouvé</h1>
          <Link to="/blog" className="btn-primary">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Retour au blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Hero */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-br from-dark-900 via-primary-900 to-dark-900 overflow-hidden">
        <div className="absolute inset-0 hero-pattern opacity-10" />
        
        <div className="container-custom relative z-10">
          <Link 
            to="/blog" 
            className="inline-flex items-center gap-2 text-primary-300 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour au blog
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl"
          >
            {article.category && (
              <span className="badge-primary mb-4">{article.category}</span>
            )}
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              {article.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-6 text-dark-300">
              <span className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                {formatDate(article.published_at || article.created_at)}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                5 min de lecture
              </span>
              {article.author_name && (
                <span>Par {article.author_name}</span>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="section bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-4 gap-12">
            {/* Main content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="lg:col-span-3"
            >
              {article.image_url && (
                <img 
                  src={article.image_url} 
                  alt={article.title}
                  className="w-full aspect-video object-cover rounded-2xl mb-8"
                />
              )}

              <div 
                className="article-content"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Tags */}
              {article.tags && article.tags.length > 0 && (
                <div className="mt-12 pt-8 border-t border-dark-100">
                  <h3 className="font-semibold text-dark-900 mb-4">Tags</h3>
                  <div className="flex flex-wrap gap-2">
                    {article.tags.map((tag, index) => (
                      <span key={index} className="badge bg-dark-100 text-dark-600">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Share */}
              <div className="mt-8 pt-8 border-t border-dark-100">
                <h3 className="font-semibold text-dark-900 mb-4">Partager cet article</h3>
                <div className="flex gap-3">
                  <a 
                    href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(article.title)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-[#0077b5] rounded-lg flex items-center justify-center text-white hover:opacity-90 transition-opacity"
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                  <a 
                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(article.title)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-[#1da1f2] rounded-lg flex items-center justify-center text-white hover:opacity-90 transition-opacity"
                  >
                    <Twitter className="w-5 h-5" />
                  </a>
                  <a 
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-[#1877f2] rounded-lg flex items-center justify-center text-white hover:opacity-90 transition-opacity"
                  >
                    <Facebook className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="sticky top-24 space-y-6"
              >
                {/* Author */}
                <div className="card p-6">
                  <h3 className="font-semibold text-dark-900 mb-4">Auteur</h3>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-primary-600 font-semibold text-lg">
                        {article.author_name?.charAt(0) || 'W'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-dark-900">{article.author_name || 'WealthTech Pro'}</p>
                      <p className="text-sm text-dark-500">Expert WealthTech</p>
                    </div>
                  </div>
                </div>

                {/* CTA */}
                <div className="card p-6 bg-primary-50 border-primary-100">
                  <h3 className="font-semibold text-dark-900 mb-2">Besoin d'accompagnement ?</h3>
                  <p className="text-dark-600 text-sm mb-4">
                    Contactez-nous pour discuter de vos projets.
                  </p>
                  <Link to="/contact" className="btn-primary w-full btn-sm">
                    Nous contacter
                  </Link>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ArticlePage;
